import List from "@mui/material/List";
import { Navigation } from "../../types";
import { MINI_DRAWER_WIDTH } from "../../shared";
import ListSubheader from "@mui/material/ListSubheader";
import { getDrawerSxTransitionMixin } from "../utils";
import Divider from "@mui/material/Divider";
import { useState } from "react";
import { useLocation } from "react-router";
import { SidebarPageItem } from "./SidebarPageItem";

interface PropTypes {
    navigation: Navigation;
    depth?: number;
    isMini?: boolean;
    isPopover?: boolean;
    isFullyExpanded?: boolean;
    isFullyCollapsed?: boolean;
    hasDrawerTransitions?: boolean;
    sidebarExpandedWidth: number | string;
    onLinkClick: () => void;
}

function SidebarNavigation({
    navigation,
    depth = 0,
    isMini = false,
    isPopover = false,
    isFullyExpanded = true,
    isFullyCollapsed = false,
    hasDrawerTransitions = false,
    sidebarExpandedWidth,
    onLinkClick
}: PropTypes) {

    // dropdown open state
    const [openMap, setOpenMap] = useState<Record<string, boolean>>({});

    // current route
    const location = useLocation();

    // helper to toggle the expanded state for a specific dropdown
    const handleToggleDropdown = (key: string) => {
        setOpenMap(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // TODO auto open dropdowns containing paths mathing location.pathname

    return (
        <List
            sx={{
                padding: 0,
                mt: isPopover && depth === 1 ? 0.5 : 0,
                mb: depth === 0 && !isPopover ? 4 : 0.5,
                pl: (isPopover ? 1 : 2) * (isPopover ? depth - 1 : depth),
                minWidth: isPopover && depth === 1 ? 240 : 'auto',
                width: isMini ? MINI_DRAWER_WIDTH : 'auto',
            }}
        >
            {navigation.map((item, index) => {

                // if the current item is a header
                if (item.kind === 'header') {
                    return (
                        <ListSubheader
                            key={`subheader-${depth}-${index}`}
                            sx={{
                                fontSize: 12,
                                fontWeight: '700',
                                height: isMini ? 0 : 40,
                                ...(hasDrawerTransitions && getDrawerSxTransitionMixin(isFullyExpanded, 'height')),
                                px: 2,
                                minWidth: sidebarExpandedWidth,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                zIndex: 2,
                            }}
                        >
                            {item.title}
                        </ListSubheader>
                    );
                }

                // if the current item is a divider
                if (item.kind === 'divider') {
                    // get the next navigation item
                    const nextItem = navigation[index + 1];

                    return (
                        <li key={`divider-${depth}-${index}`}>
                            <Divider
                                sx={{
                                    borderBottomWidth: 2,
                                    mx: 1,
                                    mt: 1,
                                    mb: nextItem?.kind === 'header' && !isMini ? 0 : 1,
                                    ...(hasDrawerTransitions && getDrawerSxTransitionMixin(isFullyExpanded, 'margin')),
                                }}
                            />
                        </li>
                    );
                }

                const key = `page-${depth}-${index}`;

                return (
                    <SidebarPageItem
                        key={key}
                        id={key}
                        item={item}
                        expanded={!!openMap[key]}
                        isMini={isMini}
                        isFullyExpanded={isFullyExpanded}
                        isFullyCollapsed={isFullyCollapsed}
                        selected={item.to === location.pathname}
                        disabled={false}
                        onClick={(itemId, item) => {
                            // if a dropdown item was clicked
                            if (item.kind === 'dropdown') {
                                // toggle the expanded state
                                handleToggleDropdown(itemId);
                            } else {
                                onLinkClick()
                            }
                        }}
                        renderNestedNavigation={() => (
                            <SidebarNavigation
                                navigation={item.children ?? []}
                                depth={depth + 1}
                                isPopover={isMini}
                                sidebarExpandedWidth={sidebarExpandedWidth}
                                onLinkClick={onLinkClick}
                            />
                        )}
                    />
                );
            })}
        </List>
    );
}

export { SidebarNavigation };